import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { API, authHeaders, formatErr, newId } from "./api";
import { inputCls, Field, SaveBar, TabCard, Toggle, SortableList, SortableItem, DeleteButton } from "./fields";
import { serviceGroups } from "../../data/siteContent";

const UNITS = [
  { value: "", label: "No unit" },
  { value: "one-time", label: "one-time" },
  { value: "/month", label: "/month" },
  { value: "from", label: "from" },
];

const blankPackage = (order) => ({
  id: newId(), group: "brand", name: "", price: "", unit: "one-time", timeline: "", note: "",
  inclusions: [], visible: true, homepage: false, chip_text: "", order,
});

export default function PricesTab({ content, token, onSaved, onUnauthorized }) {
  const [packages, setPackages] = useState(content.packages);
  const [gst, setGst] = useState(content.gst_note);
  const [chips, setChips] = useState(content.home_chips);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPackages(content.packages);
    setGst(content.gst_note);
    setChips(content.home_chips);
  }, [content]);

  const update = (id, patch) => setPackages(packages.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const remove = (id) => setPackages(packages.filter((p) => p.id !== id));

  const save = async () => {
    for (const p of packages) {
      if (p.visible && !String(p.price).trim()) return toast.error(`Price must not be empty for visible package "${p.name || "unnamed"}".`);
      if (!String(p.name).trim()) return toast.error("Every package needs a name.");
    }
    const ordered = packages.map((p, i) => ({ ...p, inclusions: p.inclusions.filter((x) => x.trim()), order: i }));
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/content/prices`, { packages: ordered, gst_note: gst, home_chips: chips }, authHeaders(token));
      onSaved(data);
      toast.success("Saved — live on the website now.");
    } catch (err) {
      if (err.response?.status === 401) return onUnauthorized();
      toast.error(formatErr(err, "Save failed. Try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <TabCard title="Prices" sub="One price list — feeds the Pricing page, service pages and homepage chips" testid="tab-prices">
      <Field label="GST note" hint="Shown next to package headings and on the Pricing page">
        <input value={gst} onChange={(e) => setGst(e.target.value)} data-testid="prices-gst-note" className={inputCls} />
      </Field>

      <div className="mt-8">
        <p className="mb-4 text-xs uppercase tracking-[0.2em] text-faint">Homepage chip overrides — optional custom text per service card</p>
        <div className="grid gap-5 sm:grid-cols-3">
          {serviceGroups.slice(0, 3).map((g) => (
            <Field key={g.key} label={`${g.label} chip`} hint="Empty = built from ticked packages">
              <input value={chips[g.key] || ""} onChange={(e) => setChips({ ...chips, [g.key]: e.target.value })} data-testid={`chip-input-${g.key}`} className={inputCls} />
            </Field>
          ))}
        </div>
      </div>

      <div className="mt-10">
        <p className="mb-4 text-xs uppercase tracking-[0.2em] text-faint">Packages — drag the handle to reorder</p>
        <SortableList items={packages} onReorder={setPackages} testid="packages-list">
          {(p) => (
            <SortableItem key={p.id} item={p} testid={`package-edit-${p.id}`}>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="Name"><input value={p.name} onChange={(e) => update(p.id, { name: e.target.value })} data-testid={`pkg-name-${p.id}`} className={inputCls} /></Field>
                <Field label="Service group">
                  <select value={p.group} onChange={(e) => update(p.id, { group: e.target.value })} data-testid={`pkg-group-${p.id}`} className={inputCls}>
                    {serviceGroups.map((g) => <option key={g.key} value={g.key}>{g.label}</option>)}
                  </select>
                </Field>
                <Field label="Price"><input value={p.price} onChange={(e) => update(p.id, { price: e.target.value })} placeholder="₹4,999" data-testid={`pkg-price-${p.id}`} className={inputCls} /></Field>
                <Field label="Unit">
                  <select value={p.unit} onChange={(e) => update(p.id, { unit: e.target.value })} data-testid={`pkg-unit-${p.id}`} className={inputCls}>
                    {UNITS.map((u) => <option key={u.value} value={u.value}>{u.label}</option>)}
                  </select>
                </Field>
                <Field label="Timeline"><input value={p.timeline} onChange={(e) => update(p.id, { timeline: e.target.value })} placeholder="10 working days" data-testid={`pkg-timeline-${p.id}`} className={inputCls} /></Field>
                <Field label="Short note" hint="Optional — e.g. First 5 clients, then ₹19,999"><input value={p.note} onChange={(e) => update(p.id, { note: e.target.value })} data-testid={`pkg-note-${p.id}`} className={inputCls} /></Field>
                <div className="sm:col-span-2">
                  <Field label="Inclusions" hint="One per line">
                    <textarea rows={3} value={p.inclusions.join("\n")} onChange={(e) => update(p.id, { inclusions: e.target.value.split("\n") })} data-testid={`pkg-inclusions-${p.id}`} className={`${inputCls} resize-none`} />
                  </Field>
                </div>
                <Field label="Custom chip text" hint="Optional — used when building the homepage chip">
                  <input value={p.chip_text} onChange={(e) => update(p.id, { chip_text: e.target.value })} data-testid={`pkg-chip-${p.id}`} className={inputCls} />
                </Field>
                <div className="flex items-end justify-between gap-4 pb-1">
                  <div className="flex gap-6">
                    <label className="flex items-center gap-2.5 text-xs uppercase tracking-[0.15em] text-mut">
                      <Toggle checked={p.visible} onChange={(v) => update(p.id, { visible: v })} testid={`pkg-visible-${p.id}`} label="Visible" /> Show
                    </label>
                    <label className="flex items-center gap-2.5 text-xs uppercase tracking-[0.15em] text-mut">
                      <Toggle checked={p.homepage} onChange={(v) => update(p.id, { homepage: v })} testid={`pkg-homepage-${p.id}`} label="Show on homepage" /> Homepage
                    </label>
                  </div>
                  <DeleteButton onClick={() => remove(p.id)} testid={`pkg-delete-${p.id}`} />
                </div>
              </div>
            </SortableItem>
          )}
        </SortableList>
        <button
          type="button"
          onClick={() => setPackages([...packages, blankPackage(packages.length)])}
          data-testid="pkg-add"
          className="mt-4 flex items-center gap-2 border border-dashed border-line px-5 py-3 text-xs uppercase tracking-[0.15em] text-mut transition-colors duration-300 hover:border-myrtle hover:text-pop"
        >
          <Plus size={14} /> Add Package
        </button>
      </div>

      <SaveBar onSave={save} saving={saving} lastSaved={content.saved_at?.prices} testid="prices-save" />
    </TabCard>
  );
}
